import { Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';

import { ConfirmationService } from 'primeng/api';

import { SensorFailure } from '../../../common/model';
import { AuthService, CacheService, GrowlService, WorkflowService } from '../../../common/service';
import { AppComponent } from '../../../app.component';
import { Permission } from '../../../common/enum';
import { catchError, tap } from 'rxjs/operators';
import { InputTextModule } from 'primeng/inputtext';
import { Textarea } from 'primeng/inputtextarea';
import { ButtonDirective } from 'primeng/button';
import { TooltipModule } from 'primeng/tooltip';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ConfirmDialogModule } from 'primeng/confirmdialog';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  templateUrl: './sensor-replace.component.html',
  styleUrls: ['./sensor-replace.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [FormsModule, ReactiveFormsModule, InputTextModule, ButtonDirective, TooltipModule, ScrollPanelModule, ConfirmDialogModule]
})
export class SensorReplaceComponent implements OnInit {
  Permission = Permission;
  editForm: FormGroup;
  pod_serial_number: FormControl;
  location_name: FormControl;
  project_name: FormControl;
  owner_name: FormControl;
  distributor_name: FormControl;
  sensor_serial_number: FormControl;
  sensor_type: FormControl;
  fail_reason: FormControl;
  failure_date: FormControl;
  installation_date: FormControl;
  age_in_months: FormControl;
  replacement_sensor_serial: FormControl;
  alert_status_description: FormControl;

  sensorFailure: SensorFailure;
  failure_date_formatted: string;
  installation_date_formatted: string;
  notes: string;
  failNumber: number;
  failParm: string;

  constructor(
    private app: AppComponent,
    @Inject(AuthService) public authService: AuthService,
    private confirmationService: ConfirmationService,
    @Inject(GrowlService) public growlService: GrowlService,
    private route: ActivatedRoute,
    private router: Router,
    private workflowService: WorkflowService,
    @Inject(CacheService) public cacheService: CacheService) { }

  ngOnInit() {
    this.sensorFailure = new SensorFailure();
    this.failNumber = 377;
    // this.failNumber = this.cacheService.GetLocal('Sensor-Replacement');
    if (this.failNumber) {
      this.findFail();
    } else {
      this.route.queryParams.subscribe(params => {
        this.failParm = params['id'];
        this.login(this.failParm);
      });
    }

    this.editForm = new FormGroup({
      pod_serial_number: new FormControl(this.sensorFailure.pod_serial_number),
      sensor_serial_number: new FormControl(this.sensorFailure.sensor_serial_number),
      sensor_type: new FormControl(this.sensorFailure.sensor_type),
      replacement_sensor_serial: new FormControl(this.sensorFailure.replacement_sensor_serial)
    });
  }

  login(token) {
    this.authService.clearAuth();
    this.authService.oneTimeToken(token).pipe(tap((resp) => {
      if (resp) {
        this.failNumber = this.authService.getTokenId();
        this.findFail();
        this.app.sessionStart();
      } else {
        this.growlService.error('Invalid token');
        this.router.navigate([`login`]);
      }
    }), catchError((error) => {
      this.growlService.error(error);
      return error;
    })).subscribe();
  }

  findFail() {
    if (this.failNumber === null || this.failNumber === undefined) {
      this.growlService.error('Email link is "one time only" - please use latest email');
      this.authService.logout();
    } else {
      this.workflowService.getSensorFailure(this.failNumber).pipe(tap((r) => {
        this.sensorFailure = r;
        if (this.sensorFailure.alert_status > 39) {
          const replacedStatus = [45, 50, 60];
          if (replacedStatus.includes(this.sensorFailure.alert_status)) {
            this.growlService.error(`Sensor has already been replaced!`);
          } else {
            this.growlService.error(`Incorrect status - ${this.sensorFailure.alert_status_description}.`);
          }
          this.authService.logout();
        }
        if (this.sensorFailure.replacement_sensor_serial === null) {
          this.growlService.error(`No replacement sensor allocated!`);
          this.authService.logout();
        }

      }), catchError((error) => {
        this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
        return error;
      })).subscribe();
    }
  }

  sensorReplaced() {
    this.confirmationService.confirm({
      message: `Has the sensor now been physically installed in the pod?`,
      accept: () => {
        if (this.sensorFailure.replacement_sensor_serial !== null) {
          this.growlService.info(`Replacement sensor  ${this.sensorFailure.replacement_sensor_serial}`);
          this.sensorFailure.shipped = (this.sensorFailure.replacement_sensor_serial !== null);
          this.sensorFailure.not_needed = false;
          this.sensorFailure.not_required = false;
          this.workflowService.postReplaceSensor(this.sensorFailure).pipe(tap((r) => {
            this.growlService.success('Successful');
            this.authService.logout();
          }), catchError((error) => {
            this.growlService.error(`Pod ${this.sensorFailure.pod_serial_number}, Sensor ${this.sensorFailure.sensor_serial_number} : ${error};`);
            return error;
          })).subscribe();
        } else {
          this.growlService.error(`Please add a replacement sensor serial number.`);
        }
      },
      reject: () => { 'Rejected'; },
    });
  }

  cancel() {
    this.confirmationService.confirm({
      message: `Please Note:<BR />The link on the email can only be used once!<BR /><BR /> Are you sure you want to EXIT?`,
      accept: () => {
        this.authService.logout();
      },
      reject: () => { 'Rejected'; },
    });
  }

  has(requiredPermissions: Permission | Permission[]) {
    return this.authService.hasPermission(requiredPermissions);
  }
}
